/* @flow */
import qs from 'qs'
import type { Poll, CheckedoutPoll, PollId } from './types'

// A heads-up: NODE_ENV is always set by create-react-app thus can't be
// overridden. By convention when you do a `npm run build` this will always be
// set to 'production'.
// REACT_APP_BUILD_ENV will win if set
const env = process.env.REACT_APP_BUILD_ENV || process.env.NODE_ENV

export const TOOL_URL: string =
  env === 'production'
    ? 'https://tool.opinary.com'
    : 'https://tool.staging.opinary.com'

export const EDITOR_API_URL: string =
  env === 'production'
    ? 'https://editor.opinary.com'
    : 'https://editor-staging.opinary.com'

export const GATEWAY_API_URL: string =
  env === 'production'
    ? 'https://api.opinary.com'
    : 'https://api-staging.opinary.com'

export const POLL_BASE_URL: string =
  env === 'production'
    ? 'https://compass.pressekompass.net/compasses'
    : 'https://staging.pressekompass.net/compasses'

export const REDIRECT_QUERY_PARAM_KEY = 'redirectedFrom'

export const API_BRANDS_REPORT: string = `${GATEWAY_API_URL}/v1/brandreport`

export const API_PUBLISHERS_REPORT: string = `${GATEWAY_API_URL}/v1/publisherreport`

export const API_PUBLISHERS_REVENUE: string = `${API_PUBLISHERS_REPORT}/revenue`

export const POLL_PREVIEW_URL: string = `https://clients.opinary.com/preview/index.html?url=${POLL_BASE_URL}`

export const API_LOGIN_SCOPE_URL =
  env === 'production'
    ? 'dashboard.opinary.com'
    : 'dashboard-staging.opinary.com'
export const API_LOGIN_URL: string = `${GATEWAY_API_URL}/v1/login`
export const API_LOGOUT_URL: string = `${GATEWAY_API_URL}/v1/logout`
export const API_POLLS_ENGAGEMENT_URL: string = `${GATEWAY_API_URL}/v1/polls-engagement`
export const API_AUTOMATION_ARTICLES_URL: string = `${GATEWAY_API_URL}/v1/analytics/automation`

export const API_POLLS_URL: string = `${EDITOR_API_URL}/v2/tools`
export const API_COPY_POLL_URL: string = `${EDITOR_API_URL}/v2/tools/copy`
export const API_ICONS_URL: string = `${EDITOR_API_URL}/v2/media_icons`
export const API_POLLS_V2_URL: string = `${EDITOR_API_URL}/v2/polls`
export const API_TAGS: string = `${EDITOR_API_URL}/v2/polltags`

export const API_CUSTOMERS: string = `${EDITOR_API_URL}/v2/customers?page_size=2000`

export const API_CUSTOMER_VERTICALS: string = `${EDITOR_API_URL}/v2/verticals`

export const API_BLACKLIST: string = `${EDITOR_API_URL}/v2/automation/blacklistedendpoints`

export const API_ADMIN_URL: string = `${EDITOR_API_URL}/_/admin/`

export const OLD_LIBRARY_URL: string = '/library'
export const LIBRARY_URL_ALL: string = `${OLD_LIBRARY_URL}/all`
export const LIBRARY_URL_RECOMMENDED: string = `${OLD_LIBRARY_URL}/recommended`
export const POLLS_URL: string = '/polls'
export const CAMPAIGNS_URL: string = `${EDITOR_API_URL}/_/admin/campaign/`
export const SETTINGS_URL: string = '/settings'
export const OLD_LOGIN_URL: string = '/login'
export const LOGIN_URL: string = `${TOOL_URL}/login`
export const LOGOUT_URL: string = `${TOOL_URL}/logout`
export const LIBRARY_URL: string = `${TOOL_URL}/library?${REDIRECT_QUERY_PARAM_KEY}=dashboard`
export const VERIFY_URL: string = `${TOOL_URL}/verify?${REDIRECT_QUERY_PARAM_KEY}=dashboard`
export const AUTOMATION_MATCHED_URL = `${TOOL_URL}/customer/automation-matched`
export const ANALYTICS_URL: string = '/analytics'
export const REPORT_URL: string = '/report'
export const PUBLISHER_REPORT_URL: string = '/report/publisher'
export const PUBLISHER_REVENUE_URL: string = '/report/revenue'
export const AUTOMATION_ARTICLES_URL: string = '/automation/articles'
export const AUTOMATION_BLACKLIST_URL: string = '/automation/blacklist'
export const NEW_POLL_URL: string = '/new-poll'
export const AUTOMATIONS_URL: string = `${OLD_LIBRARY_URL}/automations`

export const ADD_QUICKY_URL: string = `${API_ADMIN_URL}automation/recommendationtopic/add/`

export function apiUrlForPolls(
  featured: boolean = false,
  customer: ?string,
  search: ?string,
  tags: ?Array<Object>,
  automation: boolean = false
) {
  const options = {}
  if (featured) options.featured = true
  if (customer) options.customer = customer
  if (search) options.q = search
  if (automation) options.automation = 1

  if (tags && tags.length) {
    tags.forEach(tag => {
      if (tag.active) options[tag.label] = tag.active
    })
  }

  const optionsString = qs.stringify(options, {
    arrayFormat: 'repeat',
  })
  return `${API_POLLS_V2_URL}?${optionsString}`
}

export function apiUrlForFeaturedPolls(customer: ?string = null) {
  return apiUrlForPolls(true, customer)
}

export const apiUrlForAutomatedPolls = (customer: string) => {
  return customer
    ? `${API_POLLS_V2_URL}?automation=1&customer=${customer}`
    : `${API_POLLS_V2_URL}?automation=1`
}

export function apiUrlForPollWhereId(id: PollId): string {
  return `${API_POLLS_URL}/${id}`
}

export function apiUrlV2ForPollWhereId(id: PollId): string {
  return `${API_POLLS_V2_URL}/${id}`
}

export function urlForNewPollWherePollTypeId(id: string): string {
  return `${NEW_POLL_URL}/${id}`
}

export function urlForPollAnalyticsWhereId(id: string): string {
  return `${POLLS_URL}/${id}/analytics`
}

export function urlForPollWhereId(id: string): string {
  return `${POLLS_URL}/${id}`
}

export function pollPreviewUrl(poll: Poll | CheckedoutPoll): string {
  if (poll.customer == null) {
    throw new Error("can't create preview url for poll without customer id")
  }

  return `${POLL_PREVIEW_URL}/${poll.customer}/${poll.name}`
}

export function pollCopyUrl(poll: Poll | CheckedoutPoll): string {
  if (poll.customer == null) {
    throw new Error("can't create copy url for poll without customer id")
  }

  return `${POLL_BASE_URL}/${poll.customer}/${poll.name}`
}

export function pollEmbedUrl(poll: Poll | CheckedoutPoll): string {
  if (poll.customer == null) {
    throw new Error("can't create preview url for poll without customer id")
  }

  return `${POLL_BASE_URL}/${poll.customer}/${poll.name}`
}



// WEBPACK FOOTER //
// ./src/urls.js